import { Grid, Paper, Typography, Tabs, Tab } from '@mui/material';
import { Box } from '@mui/system';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import CircularProgress from '@mui/material/CircularProgress';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchAndNextCensusSummary, exportAndNextCensusSummary  } from '../WidgetService';
import { parentUrl } from '../../../utils/constants';
import { isDefaultDate } from '../../../utils/common';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { dateFormatter } from '../../../utils/timeFormat';
import '../Widgets.css';

const AndNextCensusSummaryDetail = () => {
    const history = useNavigate();
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const { andNextCensusTab, setAndNextCensusTab } = useWidgetContext();

    const getSummary = async () => {
        setIsFetched(false);

        const response = await fetchAndNextCensusSummary();

        setRows(response);

        const filterResponse = filterRowsByTab(response, andNextCensusTab);

        setFilteredRows(filterResponse);
        setIsFetched(true);
    };

    useEffect(() => {
        getSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterRowsByTab = (data: any[], tab: number) => {
        switch (tab) {
            case 1:
                return data.filter((member) => member.Status === MemberStatusEnum.UndeterminedDescription);

            case 2:
                return data.filter((member) => member.Status === MemberStatusEnum.ScheduledDescription);

            case 3:
                return data.filter((member) => member.Status === MemberStatusEnum.AndNextInProgressDescription);

            case 4:
                return data.filter((member) => member.Status === MemberStatusEnum.AndNextClosedDescription);

            case 5:
                return data.filter((member) => member.Status === MemberStatusEnum.PreparingForGraduationDescription);

            case 6:
                return data.filter((member) => member.Status === MemberStatusEnum.DisenrolledFromCHPDescription);

            default:
                return [];
        }
    };

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setAndNextCensusTab(newValue);

        const filterResponse = filterRowsByTab(rows, newValue);

        setFilteredRows(filterResponse);
        setPage(0);
    };

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportAndNextCensusSummary();
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `AndNextSummary - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const getStatusCount = (status: string) => rows.filter((member) => member.Status === status).length;

    const columns: GridColDef[] = [
        {
            field: 'AhcccsId',
            headerName: 'Ahcccs Id',
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            flex: 1,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            flex: 1,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : dateFormatter(new Date(row.DOB))}`,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            flex: 1,
        },
        ...(andNextCensusTab !== 1 && andNextCensusTab !== 2
            ? [
                  {
                      field: 'DateOfOnboard',
                      headerName: 'Date Of Onboard',
                      flex: 1,
                      valueGetter: (value, row) => (isDefaultDate(row.DateOfOnboard) ? '-' : dateFormatter(new Date(row.DateOfOnboard))),
                  },
              ]
            : []),
        ...(andNextCensusTab === 5
            ? [
                  {
                      field: 'ReferralDate',
                      headerName: 'Date of Graduation',
                      flex: 1,
                      valueGetter: (value, row) => (isDefaultDate(row.ReferralDate) ? '-' : dateFormatter(new Date(row.ReferralDate))),
                  },
              ]
            : []),
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={10.8} paddingBottom={1} alignItems={'center'} display={'flex'}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            andNext Census Summary
                        </Typography>
                    </Grid>
                    <Grid size={1.2}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto', float: 'right' }}
                            onClick={() => exportDetail()}
                            variant="contained"
                            color="success"
                            disabled={rows.length ? false : true}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isFetched ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid size={12} display={'flex'}>
                                    <Tabs
                                        sx={{ display: 'inline-flex', marginRight: 'auto' }}
                                        value={andNextCensusTab}
                                        onChange={handleAlertsTabChange}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        <Tab value={1} label={`Undetermined (${getStatusCount(MemberStatusEnum.UndeterminedDescription)})`} />
                                        <Tab value={2} label={`Scheduled (${getStatusCount(MemberStatusEnum.ScheduledDescription)})`} />
                                        <Tab value={3} label={`andNext In Progress (${getStatusCount(MemberStatusEnum.AndNextInProgressDescription)})`} />
                                        <Tab value={4} label={`andNext Closed (${getStatusCount(MemberStatusEnum.AndNextClosedDescription)})`} />
                                        <Tab
                                            value={5}
                                            label={`Preparing For Graduation (${getStatusCount(MemberStatusEnum.PreparingForGraduationDescription)})`}
                                        />
                                        <Tab value={6} label={`Disenrolled From CHP (${getStatusCount(MemberStatusEnum.DisenrolledFromCHPDescription)})`} />
                                    </Tabs>
                                </Grid>
                                <Grid size={12} p={2}>
                                    <div style={{ width: '100%', height: 'calc(100vh - 250px)', maxHeight: '500px' }}>
                                        <DataGrid
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                            }}
                                            rows={filteredRows}
                                            columns={columns}
                                            paginationModel={{ pageSize: pageSize, page: page }}
                                            pageSizeOptions={[5, 15]}
                                            onPaginationModelChange={(model) => {
                                                setPageSize(model.pageSize);
                                                setPage(model.page);
                                            }}
                                            getRowId={(row) => row.Id}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                                [`& .green`]: {
                                                    color: 'green',
                                                },
                                                [`& .red`]: {
                                                    color: 'red',
                                                },
                                                [`& .black`]: {
                                                    color: 'rgba(0,0,0,0.87)',
                                                },
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default AndNextCensusSummaryDetail;
